/* ==========================================================================
   Labor Day Sale landing page — campaign clock + analytics hooks
   --------------------------------------------------------------------------
   Two jobs, one file, one interval for the whole page:

   1. Campaign state. Liquid resolves the start/end deadlines server-side into
      absolute Unix timestamps (store timezone, explicit UTC offset) and paints
      the correct state class before this file ever runs. This script keeps the
      digits ticking and flips the state at the boundaries. A visitor whose
      device clock or timezone is wrong therefore still sees the right copy on
      first paint; only their local ticking is off, which is the best any
      client-side timer can do.

   2. Analytics. A single delegated click listener pushes the existing GTM
      dataLayer (container already bootstrapped in layout/theme.liquid). No new
      library, no second container, nothing fires inside the theme editor.
   ========================================================================== */

(function () {
  'use strict';

  /* One controller per page, no matter how many sections load this file. */
  if (window.__ldSaleController) return;

  var SECOND = 1000;
  var MINUTE = 60;
  var HOUR = 3600;
  var DAY = 86400;

  var STATE_CLASSES = [
    'ld-campaign--pre',
    'ld-campaign--live',
    'ld-campaign--final',
    'ld-campaign--ended'
  ];

  var root = document.documentElement;
  var controller = {
    clocks: [],
    timer: null,
    state: null
  };

  window.__ldSaleController = controller;

  function toInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  /* ---------------------------------------------------------------------
     State
     --------------------------------------------------------------------- */

  function resolveState(clock, now) {
    if (clock.preview && clock.preview !== 'auto') return clock.preview;
    if (!clock.startTs || !clock.endTs) return 'live';
    if (now < clock.startTs) return 'pre';
    if (now > clock.endTs) return 'ended';
    if (clock.endTs - now <= clock.finalWindow) return 'final';
    return 'live';
  }

  /* The digits a given state counts toward. In preview the real dates are
     usually months away, so a forced "final 48 hours" synthesises a plausible
     target — otherwise the merchant sees 37 days under a FINAL 48 HOURS label
     and reasonably concludes the section is broken. */
  function resolveTarget(clock, state, now) {
    var forced = clock.preview && clock.preview !== 'auto';

    if (state === 'pre') {
      if (forced && clock.startTs <= now) return now + 3 * DAY * SECOND;
      return clock.startTs;
    }

    if (state === 'final') {
      if (forced && (clock.endTs <= now || clock.endTs - now > clock.finalWindow)) {
        return now + clock.finalWindow;
      }
      return clock.endTs;
    }

    if (state === 'live') {
      if (forced && clock.endTs <= now) return now + 5 * DAY * SECOND;
      return clock.endTs;
    }

    return clock.endTs;
  }

  function applyState(state) {
    if (controller.state === state) return;

    var previous = controller.state;
    controller.state = state;

    STATE_CLASSES.forEach(function (className) {
      root.classList.remove(className);
    });
    root.classList.add('ld-campaign--' + state);

    syncConditionalContent(state);

    /* The first pass only mirrors what Liquid already painted — report a state
       change, not the initial state, which labor_day_sale_view already carries. */
    if (previous) pushEvent('labor_day_countdown_state', { campaign_state: state });
  }

  /* Liquid writes `hidden` on the elements that do not belong to the
     server-resolved state; this keeps them honest once the clock crosses a
     boundary while the visitor is still on the page. */
  function syncConditionalContent(state) {
    var nodes = document.querySelectorAll('.ld-sale-page [data-ld-when]');

    for (var i = 0; i < nodes.length; i += 1) {
      var node = nodes[i];
      var states = (node.getAttribute('data-ld-when') || '').split(/\s+/);
      var matches = states.indexOf(state) !== -1;

      if (matches) {
        node.removeAttribute('hidden');
      } else {
        node.setAttribute('hidden', '');
      }
    }
  }

  /* ---------------------------------------------------------------------
     Rendering
     --------------------------------------------------------------------- */

  function renderClock(clock, state, remaining) {
    if (clock.labelNode) {
      var label = clock.labels[state] || clock.labels.live;
      if (label && clock.labelNode.textContent !== label) {
        clock.labelNode.textContent = label;
      }
    }

    /* Never render a negative or rolling-over value. */
    if (remaining < 0) remaining = 0;

    var days = Math.floor(remaining / DAY);
    var hours = Math.floor((remaining % DAY) / HOUR);
    var minutes = Math.floor((remaining % HOUR) / MINUTE);
    var seconds = Math.floor(remaining % MINUTE);

    setValue(clock.values.days, pad(days));
    setValue(clock.values.hours, pad(hours));
    setValue(clock.values.minutes, pad(minutes));
    setValue(clock.values.seconds, pad(seconds));

    if (clock.statusNode) {
      clock.statusNode.textContent =
        days + ' days, ' + hours + ' hours, ' + minutes + ' minutes, ' + seconds + ' seconds remaining.';
    }
  }

  function setValue(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }

  function tick() {
    var now = Date.now();
    var alive = [];
    var pageState = null;

    for (var i = 0; i < controller.clocks.length; i += 1) {
      var clock = controller.clocks[i];

      if (!clock.root.isConnected) continue;
      alive.push(clock);

      var state = resolveState(clock, now);
      if (!pageState) pageState = state;

      if (state === 'ended') {
        renderClock(clock, state, 0);
        continue;
      }

      var target = resolveTarget(clock, state, now);
      /* Ceil keeps the first client tick identical to the server-rendered
         digits instead of dropping a second the moment the script boots. */
      var remaining = Math.ceil((target - now) / SECOND);
      renderClock(clock, state, remaining);
    }

    controller.clocks = alive;

    if (pageState) applyState(pageState);

    if (!alive.length) stop();
  }

  function start() {
    if (controller.timer || !controller.clocks.length) return;
    controller.timer = window.setInterval(tick, SECOND);
    tick();
  }

  function stop() {
    if (!controller.timer) return;
    window.clearInterval(controller.timer);
    controller.timer = null;
  }

  /* ---------------------------------------------------------------------
     Registration
     --------------------------------------------------------------------- */

  function registerClock(node) {
    if (!node || node.dataset.ldClockReady === 'true') return;
    node.dataset.ldClockReady = 'true';

    var labels = {};
    try {
      labels = JSON.parse(node.getAttribute('data-ld-labels') || '{}');
    } catch (error) {
      labels = {};
    }

    var clock = {
      root: node,
      /* Liquid emits Unix seconds; everything below compares against
         Date.now(), so all three are normalised to milliseconds here. */
      startTs: toInt(node.getAttribute('data-ld-start'), 0) * SECOND,
      endTs: toInt(node.getAttribute('data-ld-end'), 0) * SECOND,
      finalWindow: toInt(node.getAttribute('data-ld-final-window'), 48 * HOUR) * SECOND,
      preview: node.getAttribute('data-ld-preview') || 'auto',
      labels: labels,
      labelNode: node.querySelector('[data-ld-clock-label]'),
      statusNode: node.querySelector('[data-ld-clock-status]'),
      values: {
        days: node.querySelector('[data-ld-unit="days"] [data-ld-value]'),
        hours: node.querySelector('[data-ld-unit="hours"] [data-ld-value]'),
        minutes: node.querySelector('[data-ld-unit="minutes"] [data-ld-value]'),
        seconds: node.querySelector('[data-ld-unit="seconds"] [data-ld-value]')
      }
    };

    controller.clocks.push(clock);
    start();
  }

  function scan(scope) {
    var container = scope || document;
    var nodes = container.querySelectorAll('[data-ld-clock]');
    for (var i = 0; i < nodes.length; i += 1) {
      registerClock(nodes[i]);
    }
  }


  /* ---------------------------------------------------------------------
     Build calculator
     ---------------------------------------------------------------------
     Aggregates the selected component cards into combined gains. All data
     lives on the DOM (data-hp / data-tq / data-effects written by Liquid),
     so the module holds no state of its own and survives theme-editor
     section re-renders by simply re-scanning.
     --------------------------------------------------------------------- */

  function initConfigurator(rootEl) {
    if (!rootEl || rootEl.dataset.ldConfigReady === 'true') return;
    rootEl.dataset.ldConfigReady = 'true';

    var hpNode = rootEl.querySelector('[data-ld-config-stat="hp"]');
    var tqNode = rootEl.querySelector('[data-ld-config-stat="tq"]');
    var tqWrap = rootEl.querySelector('[data-ld-config-tq]');
    var countNode = rootEl.querySelector('[data-ld-config-stat="count"]');
    var effectsNode = rootEl.querySelector('[data-ld-config-effects]');
    var emptyNode = rootEl.querySelector('[data-ld-config-empty]');
    var badgesNode = rootEl.querySelector('[data-ld-config-badges]');
    var carChip = rootEl.querySelector('[data-ld-config-carhp]');
    var splitNode = rootEl.querySelector('[data-ld-config-split]');
    var statusNode = rootEl.querySelector('[data-ld-config-status]');

    function allParts() {
      return rootEl.querySelectorAll('[data-ld-part]');
    }

    function setPart(li, on) {
      var btn = li.querySelector('[data-ld-part-toggle]');
      if (!btn) return;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      li.classList.toggle('is-selected', on);
      if (!on) {
        var own = li.querySelector('[data-ld-own]');
        if (own) own.checked = false;
      }
    }

    function isOn(li) {
      var btn = li.querySelector('[data-ld-part-toggle]');
      return Boolean(btn && btn.getAttribute('aria-pressed') === 'true');
    }

    function recompute(toggledPart) {
      var parts = allParts();
      var hp = 0;
      var tq = 0;
      var count = 0;
      var ownHp = 0;
      var effects = [];
      var seen = {};
      var chips = [];

      for (var i = 0; i < parts.length; i += 1) {
        var li = parts[i];
        if (!isOn(li)) continue;

        count += 1;
        var partHp = toInt(li.getAttribute('data-hp'), 0);
        var partTq = toInt(li.getAttribute('data-tq'), 0);
        var own = li.querySelector('[data-ld-own]');
        var owned = Boolean(own && own.checked);

        hp += partHp;
        tq += partTq;
        if (owned) ownHp += partHp;

        var lines = (li.getAttribute('data-effects') || '').split(/\r?\n/);
        for (var j = 0; j < lines.length; j += 1) {
          var line = lines[j].replace(/^\s+|\s+$/g, '');
          if (line && !seen[line]) {
            seen[line] = true;
            effects.push(line);
          }
        }

        chips.push({ label: li.getAttribute('data-badge') || '', owned: owned });
      }

      if (hpNode) hpNode.textContent = '+' + hp;
      if (tqNode) tqNode.textContent = '+' + tq;
      if (tqWrap) tqWrap.hidden = tq === 0;
      if (countNode) countNode.textContent = String(count);

      if (carChip) {
        carChip.textContent = '+' + hp + ' whp';
        carChip.hidden = hp === 0;
      }

      if (effectsNode) {
        var old = effectsNode.querySelectorAll('[data-ld-config-item]');
        for (var k = 0; k < old.length; k += 1) {
          old[k].parentNode.removeChild(old[k]);
        }
        for (var m = 0; m < effects.length; m += 1) {
          var item = document.createElement('li');
          item.className = 'ld-config__effect';
          item.setAttribute('data-ld-config-item', '');
          item.textContent = effects[m];
          effectsNode.appendChild(item);
        }
        if (emptyNode) emptyNode.hidden = effects.length > 0;
      }

      if (badgesNode) {
        badgesNode.textContent = '';
        for (var c = 0; c < chips.length; c += 1) {
          if (!chips[c].label) continue;
          var chip = document.createElement('li');
          chip.className = 'ld-config__chip' + (chips[c].owned ? ' ld-config__chip--own' : '');
          chip.textContent = chips[c].label;
          badgesNode.appendChild(chip);
        }
        badgesNode.hidden = badgesNode.children.length === 0;
      }

      if (splitNode) {
        var addHp = hp - ownHp;
        if (ownHp > 0) {
          splitNode.hidden = false;
          splitNode.textContent =
            'Already on your car: +' + ownHp + ' whp · This build adds: +' + addHp + ' whp';
        } else {
          splitNode.hidden = true;
        }
      }

      /* Collapsed groups still tell you something is selected inside. */
      var groups = rootEl.querySelectorAll('[data-ld-config-group]');
      for (var g = 0; g < groups.length; g += 1) {
        var groupParts = groups[g].querySelectorAll('[data-ld-part]');
        var groupSelected = 0;
        for (var q = 0; q < groupParts.length; q += 1) {
          if (isOn(groupParts[q])) groupSelected += 1;
        }
        var groupChip = groups[g].querySelector('[data-ld-group-count]');
        if (groupChip) {
          groupChip.textContent = groupSelected + ' selected';
          groupChip.hidden = groupSelected === 0;
        }
      }

      if (statusNode) {
        statusNode.textContent = count + ' components selected. Combined gain +' + hp + ' whp.';
      }

      if (toggledPart) {
        pushEvent('labor_day_configurator_toggle', {
          part: toggledPart.getAttribute('data-part-name') || undefined,
          selected: toggledPart.classList.contains('is-selected'),
          campaign_state: controller.state || undefined
        });
      }
    }

    rootEl.addEventListener('click', function (event) {
      var btn = event.target.closest ? event.target.closest('[data-ld-part-toggle]') : null;
      if (!btn || !rootEl.contains(btn)) return;

      var li = btn.closest('[data-ld-part]');
      if (!li) return;

      var turningOn = !isOn(li);

      /* Tiers of one package cannot stack — selecting one clears its group. */
      if (turningOn) {
        var exclusive = li.getAttribute('data-exclusive');
        if (exclusive) {
          var parts = allParts();
          for (var i = 0; i < parts.length; i += 1) {
            if (parts[i] !== li && parts[i].getAttribute('data-exclusive') === exclusive) {
              setPart(parts[i], false);
            }
          }
        }
      }

      setPart(li, turningOn);
      recompute(li);
    });

    rootEl.addEventListener('change', function (event) {
      var target = event.target;
      if (target && target.hasAttribute && target.hasAttribute('data-ld-own')) {
        recompute(null);
      }
    });

    recompute(null);
  }

  function scanConfigurators(scope) {
    var container = scope || document;
    var nodes = container.querySelectorAll('[data-ld-configurator]');
    for (var i = 0; i < nodes.length; i += 1) {
      initConfigurator(nodes[i]);
    }
  }

  /* ---------------------------------------------------------------------
     Exhaust sound

     Each clip ships as a working native <audio controls>. This takes the
     native chrome away and drives the element from the themed transport
     instead — so the markup is never dependent on the script having run.

     One clip plays at a time across the whole page: several exhaust notes
     layered over each other is noise, not a comparison, and the visitor is
     here to tell two mufflers apart.
     --------------------------------------------------------------------- */

  var playing = null;

  function formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) return '';
    var whole = Math.floor(seconds);
    return Math.floor(whole / 60) + ':' + pad(whole % 60);
  }

  function audioReset(clip) {
    clip.el.classList.remove('ld-audio__clip--playing', 'ld-audio__clip--loading');
    clip.toggle.setAttribute('aria-pressed', 'false');
    setAudioLabel(clip, 'Play');
  }

  /* The button is the only control, so its name has to say what pressing it
     does now — aria-pressed alone reads as state, not action. */
  function setAudioLabel(clip, verb) {
    clip.toggle.setAttribute('aria-label', verb + clip.labelSuffix);
  }

  function audioFail(clip) {
    if (playing === clip) playing = null;
    audioReset(clip);
    clip.el.classList.add('ld-audio__clip--error');
    clip.toggle.disabled = true;
    if (clip.error) clip.error.hidden = false;
    if (clip.time) clip.time.textContent = '';
  }

  function initAudioClip(el, panel) {
    if (el.hasAttribute('data-ld-audio-ready')) return;

    var media = el.querySelector('[data-ld-audio]');
    var toggle = el.querySelector('[data-ld-audio-toggle]');
    if (!media || !toggle) return;

    el.setAttribute('data-ld-audio-ready', '');

    var clip = {
      el: el,
      media: media,
      toggle: toggle,
      fill: el.querySelector('[data-ld-audio-fill]'),
      time: el.querySelector('[data-ld-audio-time]'),
      error: el.querySelector('[data-ld-audio-error]'),
      name: media.getAttribute('data-ld-audio-name') || '',
      category: panel ? panel.getAttribute('data-ld-audio-category') || '' : '',
      counted: false,
      /* Everything after the verb in the button's accessible name. */
      labelSuffix: (toggle.getAttribute('aria-label') || 'Play').replace(/^Play/, '')
    };

    /* Only now is the themed transport actually driveable. */
    media.removeAttribute('controls');

    function setProgress() {
      if (!clip.fill) return;
      var total = media.duration;
      var ratio = isFinite(total) && total > 0 ? media.currentTime / total : 0;
      clip.fill.style.width = Math.min(100, Math.max(0, ratio * 100)) + '%';
    }

    function setTime() {
      if (!clip.time) return;
      var total = media.duration;
      if (!isFinite(total) || total <= 0) {
        clip.time.textContent = '';
        return;
      }
      clip.time.textContent = media.paused && !media.currentTime
        ? formatTime(total)
        : formatTime(media.currentTime) + ' / ' + formatTime(total);
    }

    toggle.addEventListener('click', function () {
      if (!media.paused) {
        media.pause();
        return;
      }

      /* Stop whatever else is running before this one starts. */
      if (playing && playing !== clip) playing.media.pause();

      playing = clip;
      el.classList.add('ld-audio__clip--loading');

      var attempt = media.play();

      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(function (reason) {
          el.classList.remove('ld-audio__clip--loading');

          /* Pausing a clip that is still opening its file rejects the pending
             play() with AbortError. That happens every time someone switches
             clips mid-load, and it says nothing about the file — only a real
             load or decode failure is worth marking the clip dead over. */
          if (reason && reason.name === 'AbortError') return;

          audioFail(clip);
        });
      }
    });

    media.addEventListener('playing', function () {
      el.classList.remove('ld-audio__clip--loading');
      el.classList.add('ld-audio__clip--playing');
      toggle.setAttribute('aria-pressed', 'true');
      setAudioLabel(clip, 'Pause');

      if (!clip.counted) {
        clip.counted = true;
        pushEvent('labor_day_exhaust_sound_play', {
          sound_name: clip.name,
          component_group: clip.category
        });
      }
    });

    media.addEventListener('waiting', function () {
      el.classList.add('ld-audio__clip--loading');
    });

    media.addEventListener('pause', function () {
      if (playing === clip) playing = null;
      audioReset(clip);
      setTime();
    });

    media.addEventListener('ended', function () {
      if (playing === clip) playing = null;
      audioReset(clip);
      media.currentTime = 0;
      setProgress();
      setTime();
    });

    media.addEventListener('timeupdate', function () {
      setProgress();
      setTime();
    });

    media.addEventListener('loadedmetadata', setTime);

    media.addEventListener('error', function () {
      audioFail(clip);
    });

    setTime();
  }

  function scanAudio(scope) {
    var container = scope || document;
    var panels = container.querySelectorAll('[data-ld-audio-panel]');

    for (var i = 0; i < panels.length; i += 1) {
      var panel = panels[i];
      var clips = panel.querySelectorAll('[data-ld-audio-clip]');

      for (var j = 0; j < clips.length; j += 1) {
        initAudioClip(clips[j], panel);
      }

      /* Swap the native players for the transport only once they are wired. */
      panel.classList.add('ld-audio--js');
    }
  }

  /* A section rebuild in the theme editor throws away the nodes we hold. */
  function stopAudioIn(scope) {
    if (playing && scope && scope.contains && scope.contains(playing.el)) {
      playing.media.pause();
      playing = null;
    }
  }

  /* ---------------------------------------------------------------------
     Analytics — attributes in the markup, one delegated listener here
     --------------------------------------------------------------------- */

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function saleContext() {
    var config = window.DMGoogleTrackingConfig || window.DMOpenAIAdsConfig || {};
    var context = config.context || {};
    var marker = document.querySelector('[data-dm-site-campaign]');
    var campaign = marker ? marker.getAttribute('data-dm-site-campaign') : '';
    var suffix = String(context.templateSuffix || '');
    var isBlackFriday = campaign === 'black_friday_2026' || /black[-_]friday/.test(suffix);
    var isLaborDay = campaign === 'labor_day_2026' || /labor[-_]day/.test(suffix);
    return {
      pageType: isBlackFriday ? 'black_friday_sale' : (isLaborDay ? 'labor_day_sale' : context.pageType || 'unknown'),
      campaign: isBlackFriday ? 'black_friday_2026' : (isLaborDay ? 'labor_day_2026' : ''),
      template: isBlackFriday ? 'black-friday-2026' : (isLaborDay ? 'labor-day-sale' : suffix || context.pageType || 'unknown')
    };
  }

  function pushEvent(name, payload) {
    if (!name || isDesignMode()) return;

    var campaign = saleContext();
    if (campaign.pageType === 'black_friday_sale') name = name.replace(/^labor_day_/, 'black_friday_');
    else if (!campaign.campaign) name = name.replace(/^labor_day_/, 'shared_');
    var data = { event: name, page_type: campaign.pageType, site_campaign: campaign.campaign };

    if (payload) {
      Object.keys(payload).forEach(function (key) {
        if (payload[key] !== undefined && payload[key] !== '') data[key] = payload[key];
      });
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    /* Technical Proof links carry their own event shape. Checked before the
       generic handler and returned early, so a proof click never doubles as a
       component toggle or a CTA event. */
    var proof = target.closest('[data-technical-proof-category]');
    if (proof) {
      pushEvent('technical_proof_click', {
        category: proof.getAttribute('data-technical-proof-category') || undefined,
        proof_title: proof.getAttribute('data-technical-proof-title') || undefined,
        destination_url: proof.getAttribute('href') || undefined,
        template: saleContext().template,
        campaign_state: controller.state || undefined
      });
      return;
    }

    var trigger = target.closest('[data-ld-event]');
    if (trigger) {
      pushEvent(trigger.getAttribute('data-ld-event'), {
        cta_label: trigger.getAttribute('data-ld-label') || undefined,
        cta_section: trigger.getAttribute('data-ld-section') || undefined,
        campaign_state: controller.state || undefined
      });
      return;
    }

    /* The lazy YouTube facade lives in the shared video section script; this
       only observes the same click so play events are reported too. */
    var poster = target.closest('.ld-sale-page .yt-video-grid__poster');
    if (poster) {
      var media = poster.closest('[data-yt-facade]');
      pushEvent('labor_day_video_play', {
        video_title: media ? media.getAttribute('data-title') || undefined : undefined,
        video_id: media ? media.getAttribute('data-video-id') || undefined : undefined,
        campaign_state: controller.state || undefined
      });
    }
  });

  /* ---------------------------------------------------------------------
     In-page links — land below the sticky bars, and stay landed
     ---------------------------------------------------------------------
     A native #anchor jump put the target's top edge at y=0, under the sticky
     header and the section nav, so a button read as landing "a bit high". The
     page then moved under it: sections with content-visibility:auto take their
     real height only as the scroll passes them, and the Loox wall grows once it
     loads. Here the jump is ours: real heights first, the sticky bars cleared,
     and the landing held in place until the page stops moving or the visitor
     takes over.

     The two bars move in opposite directions: the theme header slides away on
     the way down and returns on the way up, the section nav does the reverse.
     A jump down lands under the nav, a jump up under the header. A landing is
     only corrected when the target is covered or far below that line, never
     nudged towards it, because a small correction the other way swaps which
     bar shows and would chase its own tail.
     --------------------------------------------------------------------- */

  var HEADER_SELECTOR = 'sticky-header, #shopify-section-header, .header-wrapper, .site-header, header';
  var STOP_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];
  var CLEARANCE = 10;
  var SLACK = 40;
  /* Farther off than both bars together could ever explain: content moved,
     so the landing is corrected however many moves it has already made. */
  var DRIFT = 240;
  var landing = null;

  function stickyHeaders() {
    var found = [];
    var nodes = document.querySelectorAll(HEADER_SELECTOR);

    for (var i = 0; i < nodes.length; i += 1) {
      var node = nodes[i];
      var position = window.getComputedStyle(node).position;
      if (position === 'sticky' || position === 'fixed' || node.tagName.toLowerCase() === 'sticky-header') found.push(node);
    }

    return found;
  }

  function sectionNav() {
    var root = document.querySelector('[data-dm-sticky-nav]');
    var bar = root ? root.querySelector('.dm-sticky-nav__bar') : null;
    if (!bar || !bar.offsetHeight) return null;

    return {
      root: root,
      bar: bar,
      holder: root.closest('[id^="shopify-section-"]') || root,
      extra: Number(root.getAttribute('data-extra-offset')) || 0
    };
  }

  function pinnedAncestor(node) {
    for (var el = node; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
      var position = window.getComputedStyle(el).position;
      if (position === 'fixed' || position === 'sticky') return el;
    }
    return null;
  }

  /* What covers the top of the viewport right now, read off the screen: the
     bottom edge of every pinned element a tap near the top would hit.
     The theme's header is pinned by behaviour rather than by its own computed
     style, and a hidden section nav ignores pointer events, so asking the page
     what is on top is the one reading both agree with. */
  function coveredBottom(target) {
    var bottom = 0;
    var xs = [Math.round(window.innerWidth / 2), 24];

    for (var y = 2; y < 260; y += 6) {
      for (var i = 0; i < xs.length; i += 1) {
        var hit = document.elementFromPoint(xs[i], y);
        var pinned = hit && !target.contains(hit) ? pinnedAncestor(hit) : null;
        if (pinned) bottom = Math.max(bottom, Math.ceil(pinned.getBoundingClientRect().bottom));
      }
    }

    return bottom;
  }

  /* What will cover it once a jump in this direction has come to rest. */
  function expectedCover(target, goingDown) {
    var nav = sectionNav();
    if (goingDown && nav && nav.root.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING) {
      return nav.extra + nav.bar.offsetHeight;
    }

    var height = 0;
    stickyHeaders().forEach(function (node) {
      if (node.offsetHeight < 200) height = Math.max(height, node.offsetHeight);
    });
    return height;
  }

  function revealLayout() {
    var root = document.documentElement;
    if (root.classList.contains('ld-layout-settled')) return;
    root.classList.add('ld-layout-settled');
    void root.offsetHeight;
  }

  /* The theme sets scroll-behavior: smooth on <html>; a correction must not animate. */
  function jumpTo(top) {
    var root = document.documentElement;
    var previous = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, Math.max(0, top));
    root.style.scrollBehavior = previous;
  }

  function settle(target) {
    if (landing) landing.stop();

    var started = Date.now();
    var lastY = -1;
    var still = 0;
    var restedAt = 0;
    var nudgedAt = 0;
    var baseline = null;
    var corrections = 0;
    var done = false;

    function stop() {
      done = true;
      STOP_EVENTS.forEach(function (type) {
        window.removeEventListener(type, stop, { capture: true });
      });
      if (landing && landing.stop === stop) landing = null;
    }

    STOP_EVENTS.forEach(function (type) {
      window.addEventListener(type, stop, { capture: true, passive: true });
    });
    landing = { stop: stop };

    (function check() {
      if (done) return;

      var now = Date.now();
      /* Long enough for a lazy embed on a slow phone connection to finish
         growing; any wheel, touch, key or click hands the page back at once. */
      if (now - started > 10000) {
        stop();
        return;
      }

      var y = window.scrollY;
      if (Math.abs(y - lastY) < 1) {
        still += 1;
      } else {
        still = 0;
        restedAt = 0;
        nudgedAt = 0;
      }
      lastY = y;

      /* Judge only once the smooth scroll has come to rest. One more scroll
         event lets the bars catch up with where the jump left them; their
         slide transitions get time to finish before anything is measured.
         That event waits for the theme header's own slide (0.2-0.25s): the
         section nav sizes its offset from the header on each scroll event,
         and one taken mid-slide keeps room for a header that has gone. */
      if (now - started > 150 && still >= 8) {
        if (!restedAt) {
          restedAt = now;
        } else if (!nudgedAt) {
          if (now - restedAt > 250) {
            nudgedAt = now;
            window.dispatchEvent(new Event('scroll'));
          }
        } else if (now - nudgedAt > 350) {
          var top = target.getBoundingClientRect().top;

          if (baseline === null) {
            var floor = coveredBottom(target) + CLEARANCE;
            var drifted = Math.abs(top - floor) > DRIFT;
            var shift = 0;
            if (top < floor - 2) {
              shift = top - floor;
            } else if (top > floor + SLACK && (corrections === 0 || drifted)) {
              /* Too far down: going down brings the section nav back, so aim
                 for the line below it rather than for what shows right now.
                 Only as the first move: the bars answer each correction by
                 swapping places, and a second try would start them swinging.
                 Unless the Loox wall grew in between and pushed it far away. */
              shift = top - expectedCover(target, true) - CLEARANCE;
            }
            if (Math.abs(shift) >= 3 && (corrections < 3 || drifted)) {
              corrections += 1;
              jumpTo(y + shift);
            } else {
              baseline = top;
            }
          } else if (Math.abs(top - baseline) > 2) {
            /* Content above grew or shrank while the page sat still: put the
               target back where the visitor saw it land. */
            jumpTo(y + top - baseline);
          }
        }
      }

      window.requestAnimationFrame(check);
    })();
  }

  function prefersReducedMotion() {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* The nav measures and scrolls in its own click listener, which runs after
     this capture-phase one: the real heights are in place before it measures. */
  document.addEventListener('click', function (event) {
    var link = event.target && event.target.closest ? event.target.closest('[data-dm-sticky-nav] .dm-sticky-nav__link') : null;
    if (!link) return;

    var target = null;
    try {
      target = link.getAttribute('data-target') ? document.querySelector(link.getAttribute('data-target')) : null;
    } catch (error) {
      target = null;
    }
    if (!target) return;

    revealLayout();
    window.setTimeout(function () {
      settle(target);
    }, 0);
  }, true);

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var link = event.target && event.target.closest ? event.target.closest('a[href^="#"]') : null;
    if (!link || !link.closest('.ld-sale-page, .dm-expert-fitment') || link.closest('[data-dm-sticky-nav]')) return;

    var id = link.getAttribute('href').slice(1);
    try {
      id = decodeURIComponent(id);
    } catch (error) {
      /* keep the raw fragment */
    }
    var target = id ? document.getElementById(id) : null;
    if (!target) return;

    event.preventDefault();

    /* A card can ask the shop section to open one category before it lands. */
    var category = link.getAttribute('data-ld-open-tab');
    if (category) {
      document.dispatchEvent(new CustomEvent('ld:open-category', { detail: { handle: category } }));
    }

    revealLayout();
    var targetTop = target.getBoundingClientRect().top;
    var top = targetTop + window.scrollY - expectedCover(target, targetTop > 0) - CLEARANCE;
    window.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });

    if (window.history && typeof window.history.replaceState === 'function') {
      window.history.replaceState(window.history.state, '', '#' + id);
    }

    /* A keyboard activation moves focus with the view, as a native jump does. */
    if (event.detail === 0) {
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
        target.classList.add('ld-landing-focus');
      }
      target.focus({ preventScroll: true });
    }

    settle(target);
  });

  /* ---------------------------------------------------------------------
     Lifecycle
     --------------------------------------------------------------------- */

  /* Background tabs throttle setInterval — resync the moment the tab returns. */
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden && controller.clocks.length) tick();
  });

  /* Theme editor swaps section markup in and out; re-scan and drop dead clocks. */
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
    scanConfigurators(event.target);
    scanAudio(event.target);
    tick();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    stopAudioIn(event.target);
    tick();
  });

  function init() {
    scan(document);
    scanConfigurators(document);
    scanAudio(document);

    if (document.querySelector('.ld-sale-page') && saleContext().campaign) {
      pushEvent('labor_day_sale_view', { campaign_state: controller.state || undefined });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
